import { zodResolver } from '@hookform/resolvers/zod';
import { add, format } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { DateRange, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { useForm } from 'react-hook-form';
import { JumpToDateSchema } from '../schema/JumpToDateSchema';
import clsx from 'clsx';

interface JumpToDateDialogProps {
    show: boolean;
    onClose: () => void;
    onChangeDate: (data: JumpToDateForm) => void;
}

interface JumpToDateForm {
    start_date: string;
    end_date: string;
}

const JumpToDateDialog: React.FC<JumpToDateDialogProps> = ({ show, onClose, onChangeDate }) => {

    const { handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<JumpToDateForm>({
        resolver: zodResolver(JumpToDateSchema),
        defaultValues: {
            start_date: format(new Date(), 'yyyy-MM-dd'),
            end_date: format(add(new Date(), { days: 30 }), 'yyyy-MM-dd'),
        },
    });

    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: add(new Date(), { days: 30 }),
            key: 'selection'
        }
    ]);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    // Watch form values to display in the input
    const startDateValue = watch('start_date');
    const endDateValue = watch('end_date');

    const calendarContainerStyle: React.CSSProperties = {
        position: 'absolute',
        zIndex: 1056, // z-index must be higher than modal's (1055)
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginTop: '8px',
        border: '1px solid #dfe2e5',
        borderRadius: '8px',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
    };

    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (selection.startDate && selection.endDate) {
            setDateRange([{
                startDate: selection.startDate,
                endDate: selection.endDate,
                key: selection.key || 'selection'
            }]);
            // Update react-hook-form values programmatically
            setValue('start_date', format(selection.startDate, 'yyyy-MM-dd'), { shouldValidate: true });
            setValue('end_date', format(selection.endDate, 'yyyy-MM-dd'), { shouldValidate: true });
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [calendarRef]);


    const handleClose = () => {
        const today = new Date();
        const futureDate = add(today, { days: 30 });
        // Reset form to default values
        reset({
            start_date: format(today, 'yyyy-MM-dd'),
            end_date: format(futureDate, 'yyyy-MM-dd'),
        });
        // Reset date range picker state
        setDateRange([{ startDate: today, endDate: futureDate, key: 'selection' }]);
        onClose();
    };

    const onSubmit = useCallback((data: JumpToDateForm) => {
        onChangeDate(data);
    }, [onChangeDate]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Jump to Date</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form id='JumpToDateForm' onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-group">
                        <label htmlFor="date" className="form-label mb-1">Select Date Range</label>
                        <div className="position-relative" ref={calendarRef}>
                            <div className="input-group">
                                <input
                                    type='text'
                                    readOnly
                                    className={clsx("form-control app-form-control rounded-1", (errors?.start_date || errors?.end_date) && 'is-invalid')}
                                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                                    // Ensure values are valid dates before formatting
                                    value={
                                        startDateValue && endDateValue
                                            ? `${format(new Date(startDateValue), 'dd MMM yyyy')} - ${format(new Date(endDateValue), 'dd MMM yyyy')}`
                                            : ''
                                    }
                                    placeholder="Select date range"
                                />
                                <span className="input-group-text" onClick={() => setIsCalendarOpen(!isCalendarOpen)} style={{ cursor: 'pointer' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                        <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                                        <path d="M16 3l0 4"></path>
                                        <path d="M8 3l0 4"></path>
                                        <path d="M4 11l16 0"></path>
                                        <path d="M8 15h2v2h-2z"></path>
                                    </svg>
                                </span>
                                {(errors?.start_date || errors?.end_date) && (
                                    <div className="invalid-feedback d-block">
                                        {typeof errors.start_date?.message === 'string' && errors.start_date.message}
                                        {typeof errors.end_date?.message === 'string' && errors.end_date.message}
                                    </div>
                                )}
                            </div>
                            {isCalendarOpen && (
                                <div style={calendarContainerStyle}>
                                    <DateRange
                                        onChange={handleDateChange}
                                        moveRangeOnFirstSelection={false}
                                        ranges={dateRange}
                                        months={1}
                                        direction="vertical"
                                        className="shadow-sm"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" type='submit' form='JumpToDateForm'>Jump</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default JumpToDateDialog;